import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { type ReactNode } from 'react';
import clsx from 'clsx';

import { pluralize } from '@/react/common/string-utils';
import { SourceId } from '@/react/portainer/gitops/sources/types';

import { Badge } from '@@/Badge';
import { Widget } from '@@/Widget';

import { ArtifactFile } from '../../types';

type Props = {
  files: Array<ArtifactFile>;
  sourceNameById: Map<SourceId, string>;
  onReorder: (files: Array<ArtifactFile>) => void;
  onRemove: (index: number) => void;
};

export function ArtifactsSummary({
  files,
  sourceNameById,
  onReorder,
  onRemove,
}: Props) {
  return (
    <Widget>
      <Widget.Taskbar className="!mb-0">
        <span className="text-sm font-semibold text-gray-11 th-highcontrast:text-white th-dark:text-white">
          {files.length} Selected {pluralize(files.length, 'File')}
        </span>
      </Widget.Taskbar>
      <Widget.Body contentClassName="!p-0">
        {files.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-gray-6 th-highcontrast:text-gray-5 th-dark:text-gray-5">
            No files selected
          </p>
        ) : (
          files.map((file, index) => (
            <FileRow
              key={`${file.sourceId}-${file.path}`}
              file={file}
              sourceName={sourceNameById.get(file.sourceId)}
              isFirst={index === 0}
              isLast={index === files.length - 1}
              onMoveUp={() => onReorder(moveItem(files, index, index - 1))}
              onMoveDown={() => onReorder(moveItem(files, index, index + 1))}
              onRemove={() => onRemove(index)}
            />
          ))
        )}
      </Widget.Body>
    </Widget>
  );
}

type FileChipProps = {
  file: ArtifactFile;
  sourceName: string | undefined;
  className?: string;
  actions?: ReactNode;
};

export function FileChip({
  file,
  sourceName,
  className,
  actions,
}: FileChipProps) {
  return (
    <div
      className={clsx(
        'flex items-center gap-2 bg-blue-1 px-3 py-2.5 th-highcontrast:bg-gray-iron-10 th-dark:bg-blue-11',
        className
      )}
    >
      <Badge type="infoSecondary" shape="rect" size="sm" className="uppercase">
        File
      </Badge>
      <span className="flex min-w-0 flex-1 flex-col">
        {sourceName && (
          <span className="truncate text-xs text-gray-6 th-highcontrast:text-gray-5 th-dark:text-gray-5">
            {sourceName} @ {file.ref}
          </span>
        )}
        <span className="truncate font-mono text-xs text-gray-11 th-highcontrast:text-white th-dark:text-white">
          /{file.path}
        </span>
      </span>
      {actions}
    </div>
  );
}

interface FileRowProps {
  file: ArtifactFile;
  sourceName: string | undefined;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

function FileRow({
  file,
  sourceName,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onRemove,
}: FileRowProps) {
  return (
    <FileChip
      file={file}
      sourceName={sourceName}
      className="border-t-0 border-r-0 border-b border-l-0 border-solid border-gray-4 th-highcontrast:border-white th-dark:border-gray-7"
      actions={
        <>
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            className="flex shrink-0 items-center justify-center rounded border-0 bg-transparent p-0.5 text-gray-6 hover:text-gray-9 disabled:cursor-not-allowed disabled:opacity-40 th-highcontrast:text-white th-dark:text-gray-6"
            aria-label={`Move /${file.path} up`}
          >
            <ChevronUp size={12} />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            className="flex shrink-0 items-center justify-center rounded border-0 bg-transparent p-0.5 text-gray-6 hover:text-gray-9 disabled:cursor-not-allowed disabled:opacity-40 th-highcontrast:text-white th-dark:text-gray-6"
            aria-label={`Move /${file.path} down`}
          >
            <ChevronDown size={12} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="flex shrink-0 items-center justify-center rounded border-0 bg-transparent p-0.5 text-gray-6 hover:text-gray-9 th-highcontrast:text-white th-dark:text-gray-6"
            aria-label={`Remove /${file.path}`}
          >
            <X size={12} />
          </button>
        </>
      }
    />
  );
}

function moveItem<T>(items: Array<T>, from: number, to: number): Array<T> {
  if (to < 0 || to >= items.length) {
    return items;
  }

  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
